'use client'

import { useState, forwardRef } from 'react'
import { cn } from '../utils/styles'
import { Info, Footprints } from 'lucide-react'

export interface ShoeSizeInputProps {
  value?: number // EU size as base
  onChange: (euSize: number) => void
  gender?: 'men' | 'women' | 'unisex'
  error?: string
  helperText?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  showConversions?: boolean
  showSizeGuide?: boolean
  showGenderSelector?: boolean
  defaultSystem?: 'eu' | 'us' | 'uk'
  className?: string
  variant?: 'default' | 'compact' | 'minimal'
}

// Shoe size conversion tables
const WOMEN_SIZE_CONVERSION = {
  35: { us: 5, uk: 2.5 },
  35.5: { us: 5.5, uk: 3 },
  36: { us: 6, uk: 3.5 },
  36.5: { us: 6.5, uk: 4 },
  37: { us: 6.5, uk: 4 },
  37.5: { us: 7, uk: 4.5 },
  38: { us: 7.5, uk: 5 },
  38.5: { us: 8, uk: 5.5 },
  39: { us: 8.5, uk: 6 },
  39.5: { us: 9, uk: 6.5 },
  40: { us: 9, uk: 6.5 },
  40.5: { us: 9.5, uk: 7 },
  41: { us: 10, uk: 7.5 },
  41.5: { us: 10.5, uk: 8 },
  42: { us: 11, uk: 8.5 },
  42.5: { us: 11.5, uk: 9 },
  43: { us: 12, uk: 9.5 },
} as const

const MEN_SIZE_CONVERSION = {
  38: { us: 6, uk: 5.5 },
  38.5: { us: 6.5, uk: 6 },
  39: { us: 7, uk: 6 },
  39.5: { us: 7.5, uk: 6.5 },
  40: { us: 7.5, uk: 7 },
  40.5: { us: 8, uk: 7.5 },
  41: { us: 8.5, uk: 8 },
  41.5: { us: 9, uk: 8.5 },
  42: { us: 9.5, uk: 9 },
  42.5: { us: 10, uk: 9.5 },
  43: { us: 10.5, uk: 10 },
  43.5: { us: 11, uk: 10.5 },
  44: { us: 11.5, uk: 11 },
  44.5: { us: 12, uk: 11.5 },
  45: { us: 12.5, uk: 12 },
  45.5: { us: 13, uk: 12.5 },
  46: { us: 13.5, uk: 13 },
  47: { us: 14.5, uk: 14 },
} as const

export const ShoeSizeInput = forwardRef<HTMLDivElement, ShoeSizeInputProps>(
  ({
    value,
    onChange,
    gender = 'unisex',
    error,
    helperText,
    label = 'Shoe Size',
    placeholder,
    disabled = false,
    showConversions = true,
    showSizeGuide = true,
    showGenderSelector = false,
    defaultSystem = 'eu',
    className,
    variant = 'default',
    ...props
  }, ref) => {
    const [selectedSystem, setSelectedSystem] = useState<'eu' | 'us' | 'uk'>(defaultSystem)
    const [isOpen, setIsOpen] = useState(false)

    const conversionTable =
      gender === 'men' ? MEN_SIZE_CONVERSION : WOMEN_SIZE_CONVERSION

    // Get all available EU sizes based on gender
    const getEUSizes = () => {
      const sizes = Object.keys(conversionTable)
        .map(Number)
        .sort((a, b) => a - b)
      return sizes
    }

    // Convert EU size to other systems
    const convertFromEU = (euSize: number, targetSystem: 'us' | 'uk') => {
      const conversion = conversionTable[euSize as keyof typeof conversionTable]
      if (!conversion) return null
      return conversion[targetSystem]
    }

    // Find EU size from US or UK size
    const convertToEU = (
      size: number,
      sourceSystem: 'us' | 'uk',
    ): number | null => {
      for (const [euSize, conversions] of Object.entries(conversionTable)) {
        if (conversions[sourceSystem] === size) {
          return Number(euSize)
        }
      }
      return null
    }

    // Get available sizes for selected system
    const getAvailableSizes = () => {
      if (selectedSystem === 'eu') {
        return getEUSizes()
      }

      const sizes = new Set<number>()
      Object.values(conversionTable).forEach((conv) => {
        sizes.add(conv[selectedSystem as 'us' | 'uk'])
      })
      return Array.from(sizes).sort((a, b) => a - b)
    }

    // Handle size selection
    const handleSizeChange = (selectedSize: number) => {
      if (selectedSystem === 'eu') {
        onChange(selectedSize)
      } else {
        const euSize = convertToEU(selectedSize, selectedSystem as 'us' | 'uk')
        if (euSize) {
          onChange(euSize)
        }
      }
    }

    // Get display value for current system
    const getDisplayValue = () => {
      if (!value) return ''

      if (selectedSystem === 'eu') {
        return String(value)
      }

      const converted = convertFromEU(value, selectedSystem as 'us' | 'uk')
      return converted ? String(converted) : ''
    }

    const getVariantStyles = () => {
      switch (variant) {
        case 'compact':
          return 'space-y-2'
        case 'minimal':
          return 'space-y-1'
        default:
          return 'space-y-4'
      }
    }

    const renderGenderSelector = () => {
      if (!showGenderSelector) return null

      return (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Gender Category
          </label>
          <div className="grid grid-cols-3 gap-1">
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "px-3 py-2 text-xs rounded-md border transition-colors",
                gender === 'women'
                  ? "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              Women's
            </button>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "px-3 py-2 text-xs rounded-md border transition-colors",
                gender === 'men'
                  ? "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              Men's
            </button>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "px-3 py-2 text-xs rounded-md border transition-colors",
                gender === 'unisex'
                  ? "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              Unisex
            </button>
          </div>
        </div>
      )
    }

    const renderSystemSelector = () => {
      if (variant === 'minimal') return null

      return (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Size System
            <Info className="h-3 w-3 cursor-help text-gray-400" title="Select your preferred sizing system. We'll automatically convert between EU, US, and UK sizes." />
          </label>
          <div className="grid grid-cols-3 gap-1">
            {['eu', 'us', 'uk'].map((system) => (
              <button
                key={system}
                type="button"
                disabled={disabled}
                onClick={() => setSelectedSystem(system as 'eu' | 'us' | 'uk')}
                className={cn(
                  "px-3 py-2 text-sm rounded-md border transition-colors",
                  selectedSystem === system
                    ? "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                {system.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )
    }

    const renderSizeSelect = () => {
      return (
        <div className="space-y-2">
          <label htmlFor="shoe-size" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label} ({selectedSystem.toUpperCase()})
          </label>
          <div className="relative">
            <select
              id="shoe-size"
              value={getDisplayValue()}
              onChange={(e) => handleSizeChange(Number(e.target.value))}
              disabled={disabled}
              className={cn(
                "w-full px-3 py-2 text-sm border rounded-md bg-white transition-colors",
                "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                "dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300",
                error && "border-red-500 focus:ring-red-500",
                disabled && "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700"
              )}
            >
              <option value="">{placeholder || `Select ${selectedSystem.toUpperCase()} size`}</option>
              {getAvailableSizes().map((size) => (
                <option key={size} value={size}>
                  {selectedSystem.toUpperCase()} {size}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {helperText && !error && <p className="text-sm text-gray-600 dark:text-gray-400">{helperText}</p>}
        </div>
      )
    }

    const renderConversions = () => {
      if (!showConversions || !value || variant === 'minimal') return null

      return (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
          <p className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Size Conversions:</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div
              className={cn(
                'rounded p-2 text-center',
                selectedSystem === 'eu' && 'bg-blue-100 dark:bg-blue-900 font-medium',
              )}
            >
              <div className="text-gray-600 dark:text-gray-400">EU</div>
              <div className="text-lg text-gray-900 dark:text-gray-100">{value}</div>
            </div>
            <div
              className={cn(
                'rounded p-2 text-center',
                selectedSystem === 'us' && 'bg-blue-100 dark:bg-blue-900 font-medium',
              )}
            >
              <div className="text-gray-600 dark:text-gray-400">US</div>
              <div className="text-lg text-gray-900 dark:text-gray-100">
                {convertFromEU(value, 'us') || '-'}
              </div>
            </div>
            <div
              className={cn(
                'rounded p-2 text-center',
                selectedSystem === 'uk' && 'bg-blue-100 dark:bg-blue-900 font-medium',
              )}
            >
              <div className="text-gray-600 dark:text-gray-400">UK</div>
              <div className="text-lg text-gray-900 dark:text-gray-100">
                {convertFromEU(value, 'uk') || '-'}
              </div>
            </div>
          </div>
        </div>
      )
    }

    const renderSizeGuide = () => {
      if (!showSizeGuide || variant === 'minimal') return null

      return (
        <div className="space-y-1 rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-xs text-gray-600 dark:text-gray-400">
          <p className="font-medium text-gray-900 dark:text-gray-100">Shoe Size Tips:</p>
          <ul className="ml-4 space-y-1">
            <li>• Measure your feet in the evening when they're largest</li>
            <li>• If between sizes, choose the larger size</li>
            <li>• Consider the width of your feet</li>
            <li>• Dance shoes may fit differently than regular shoes</li>
          </ul>
        </div>
      )
    }

    if (variant === 'minimal') {
      return (
        <div ref={ref} className={cn("space-y-2", className)} {...props}>
          {renderSizeSelect()}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700",
          getVariantStyles(),
          className
        )}
        {...props}
      >
        {variant === 'default' && (
          <div className="flex items-center gap-2 mb-4">
            <Footprints className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{label}</h3>
          </div>
        )}

        <div className={getVariantStyles()}>
          {renderGenderSelector()}
          {renderSystemSelector()}
          {renderSizeSelect()}
          {renderConversions()}
          {renderSizeGuide()}
        </div>
      </div>
    )
  }
)

ShoeSizeInput.displayName = "ShoeSizeInput"